// What HubSpot holds for a lead's landlord / tenant company, and how it changes what
// promoting a discovered person costs. Mirrors the BE `lead.hubspotMatches[role]` blob
// (building_manager_api/domain/services/lead_research/hubspot_sync.py).

import { PeopleRole } from "./peopleCandidate";

// One contact HubSpot holds at the company. `optedOut` is load-bearing rather than
// informational: an opted-out person cannot be promoted at any price.
export interface HubSpotContact {
  contactId: string;
  name: string | null;
  email: string | null;
  title: string | null;
  // The company the CRM records for them, which is NOT necessarily the lead's - a
  // difference is what "moved company" is derived from.
  companyName: string | null;
  ownerId: string | null;
  lifecycleStage: string | null;
  leadStatus: string | null;
  lastContacted: string | null;
  noteCount: number | null;
  optedOut: boolean;
}

export interface HubSpotCompany {
  companyId: string;
  name: string | null;
  domain: string | null;
  lifecycleStage: string | null;
  ownerId: string | null;
}

// The model's verdict for one discovered person. "exact" is auto-accepted; "possible"
// needs an operator click before the held address is ever used.
export type HubSpotMatchVerdict = "exact" | "possible";

export interface HubSpotMatch {
  contactId: string;
  verdict: HubSpotMatchVerdict;
  // True when the CRM has them at a different employer: we know the person, but the
  // address we hold belongs to their old job.
  moved: boolean;
  reasoning: string;
}

// The operator's answer to a fuzzy match, scoped to the pair it was about.
export interface HubSpotDecision {
  contactId: string;
  verdict: "confirmed" | "rejected";
  decidedBy: string;
  decidedAt: string;
}

// How promoting one person would be paid for. Computed on the BE and rendered directly:
// the panel never re-derives it, so the price quoted before a click is the price paid.
export interface HubSpotPromotion {
  source: "hubspot" | "blocked";
  reason?: "opted_out";
  email?: string;
  contactId?: string;
  name?: string | null;
  title?: string | null;
}

// "complete" - the read worked (even if it found nobody). The rest are why it didn't,
// kept distinct so the panel can say which rather than implying nobody is known.
export type HubSpotStatus =
  | "complete"
  | "no_company"
  | "not_configured"
  | "unavailable";

export interface HubSpotRoleResult {
  status: HubSpotStatus;
  companyName: string;
  company: HubSpotCompany | null;
  contacts: HubSpotContact[];
  matches: Record<string, HubSpotMatch>;
  decisions?: Record<string, HubSpotDecision>;
  promotion?: Record<string, HubSpotPromotion>;
  readAt: string;
  error?: string | null;
  traceRunId?: string | null;
  truncated?: boolean;
}

export type HubSpotMatches = Partial<Record<PeopleRole, HubSpotRoleResult>>;

// The relationship adorner shown against a person in BOTH the candidates list and the
// contact list. Derived here, in one place, so the two surfaces cannot disagree about
// who we already know.
export type RelationshipKind =
  | "in_hubspot"
  | "possible_match"
  | "moved"
  | "do_not_contact"
  | "apollo_only";

export interface Relationship {
  kind: RelationshipKind;
  // Where the row came from, which is a different question from the relationship:
  // an "In HubSpot" person may or may not also be in the Apollo results.
  source: "apollo_and_hubspot" | "apollo_only" | "hubspot_only";
  contact: HubSpotContact | null;
  match: HubSpotMatch | null;
  decision: HubSpotDecision | null;
  // Set when the person cannot be contacted at all.
  blockedReason?: "opted_out";
}

// A person who is in the CRM but not in the Apollo results carries a synthetic id built
// from their contact id. Mirrors the BE `HUBSPOT_ONLY_PREFIX` (hubspot_sync.py).
export const HUBSPOT_ONLY_PREFIX = "hubspot:";

export const relationshipFor = (
  apolloId: string,
  hubspot: HubSpotRoleResult | null | undefined,
  { hubspotOnly = false }: { hubspotOnly?: boolean } = {}
): Relationship => {
  const source = hubspotOnly ? "hubspot_only" : "apollo_only";
  if (!hubspot) {
    return {
      kind: "apollo_only",
      source,
      contact: null,
      match: null,
      decision: null,
    };
  }
  if (hubspotOnly) {
    // There is no match to reason about - we hold this person and Apollo simply did not
    // return them, so the relationship comes straight off the contact record.
    const known =
      (hubspot.contacts ?? []).find(
        (c) => c.contactId === apolloId.slice(HUBSPOT_ONLY_PREFIX.length)
      ) ?? null;
    return {
      kind: known?.optedOut ? "do_not_contact" : "in_hubspot",
      source: "hubspot_only",
      contact: known,
      match: null,
      decision: null,
      ...(known?.optedOut ? { blockedReason: "opted_out" as const } : {}),
    };
  }
  const match = hubspot.matches?.[apolloId] ?? null;
  const decision = hubspot.decisions?.[apolloId] ?? null;
  const contact = match
    ? ((hubspot.contacts ?? []).find((c) => c.contactId === match.contactId) ??
      null)
    : null;
  const promotion = hubspot.promotion?.[apolloId];

  if (promotion?.source === "blocked") {
    return {
      kind: "do_not_contact",
      source: hubspotOnly ? "hubspot_only" : "apollo_and_hubspot",
      contact,
      match,
      decision,
      blockedReason: promotion.reason,
    };
  }
  if (!match) {
    return { kind: "apollo_only", source, contact, match, decision: null };
  }
  const resolvedSource = hubspotOnly ? "hubspot_only" : "apollo_and_hubspot";
  if (match.moved) {
    return { kind: "moved", source: resolvedSource, contact, match, decision };
  }
  const settled =
    match.verdict === "exact" || decision?.verdict === "confirmed";
  if (!settled || decision?.verdict === "rejected") {
    return {
      kind: decision?.verdict === "rejected" ? "apollo_only" : "possible_match",
      source: resolvedSource,
      contact,
      match,
      decision,
    };
  }
  return {
    kind: "in_hubspot",
    source: resolvedSource,
    contact,
    match,
    decision,
  };
};

// A HubSpot contact can legitimately carry no name: contacts created from a logged
// email, a form fill or a list import often hold only an address. The address IS their
// identity in that case - and it is precisely the thing we already hold that Apollo
// would have charged to reveal - so showing it beats rendering "(no name)" over a row
// whose whole value is that we can email them. Shared so the candidates table and the
// promoted contact list can never disagree about what a person is called.
export const contactDisplayName = (
  contact: Pick<HubSpotContact, "name" | "email">
): string => contact.name?.trim() || contact.email?.trim() || "(no name)";

// What to call a discovered person. Apollo's free People Search masks surnames
// (`Simon Ba***r`) - that mask is the thing a paid reveal buys - but when the CRM holds
// the same person we ALREADY know their full name, so continuing to print the mask
// hides information we have. Prefer the HubSpot name wherever the identity is settled.
//
// A `possible_match` is deliberately excluded: the model thinks it might be them and an
// operator hasn't confirmed it, so borrowing that name would assert an identity we have
// not established (and the chip beneath already shows the candidate CRM name).
export const candidateDisplayName = (
  candidate: { firstName: string; lastNameMasked: string },
  relationship?: Relationship | null
): string => {
  const identitySettled =
    relationship != null &&
    ["in_hubspot", "moved", "do_not_contact"].includes(relationship.kind);
  const known = identitySettled ? relationship?.contact?.name?.trim() : "";
  return (
    known ||
    `${candidate.firstName} ${candidate.lastNameMasked}`.trim() ||
    "(no name)"
  );
};

// What promoting one person would actually do. Derived from the BE's promotion plan -
// the same plan the promote handler spends against - so the checkbox, the credit quote
// and the request payload all answer this question the same way. They previously did
// not: a HubSpot-only person had a free plan entry server-side but the table still
// suppressed their checkbox, so the people we already know were the one group the
// operator could not actually reach.
//
//   free       - we already hold a usable address; promoting costs nothing
//   chargeable - a paid Apollo reveal, and this role's own discovery found them
//   blocked    - we hold an address we must not use (opted out)
//   other_role - pooled in from the sibling persona; the role-keyed pull would drop it
export type PromotionEligibility =
  | "free"
  | "chargeable"
  | "blocked"
  | "other_role";

export const promotionEligibility = (
  promotion: Record<string, HubSpotPromotion> | undefined,
  apolloId: string,
  // Whether this role's OWN Apollo discovery returned them. Only gates the paid path:
  // a HubSpot-only person is promoted from the CRM blob and never validated against it.
  inOwnDiscovery: boolean
): PromotionEligibility => {
  const source = promotion?.[apolloId]?.source;
  if (source === "hubspot") return "free";
  if (source === "blocked") return "blocked";
  // A synthetic CRM id is not an Apollo id, so it can never be the subject of a paid
  // Apollo reveal - sending one would spend a credit on a lookup that cannot resolve.
  // It reaches here only when the CRM holds no usable address (so there is no free plan
  // entry either), which is exactly the un-promotable case.
  if (apolloId.startsWith(HUBSPOT_ONLY_PREFIX)) return "other_role";
  return inOwnDiscovery ? "chargeable" : "other_role";
};

// Row order in the candidates table. People we already know lead the list: they cost no
// credits and carry a real relationship, which is the point of merging HubSpot into it.
// Blocked rows sort last despite being known - they cannot be promoted at all, so
// leading with un-tickable rows would push the actionable ones down.
export const RELATIONSHIP_RANK: Record<RelationshipKind, number> = {
  in_hubspot: 0,
  possible_match: 1,
  moved: 2,
  apollo_only: 3,
  do_not_contact: 4,
};

// The chip copy + colours for each adorner. One map, used by the candidates table and
// the contact list, so a wording change lands in both.
export const RELATIONSHIP_CHIP: Record<
  RelationshipKind,
  { label: string; bg: string; fg: string }
> = {
  in_hubspot: { label: "In HubSpot", bg: "#d1e7dd", fg: "#0f5132" },
  possible_match: { label: "Possible match", bg: "#fff3cd", fg: "#664d03" },
  moved: { label: "Moved company", bg: "#cfe2ff", fg: "#084298" },
  do_not_contact: { label: "Do not contact", bg: "#f8d7da", fg: "#842029" },
  apollo_only: { label: "Apollo discovery", bg: "#e9ecef", fg: "#6c757d" },
};

// HubSpot returns `lastContacted` as a full ISO instant. The operator wants to know
// roughly when we last spoke, not the millisecond, so render the date alone. Falls back
// to the raw value when it won't parse - an unreadable date beats "Invalid Date".
// Exported because two surfaces render this same field (the relationship sub-line and
// the contact detail row); a second copy is how they drift apart.
export const formatLastContacted = (iso: string): string => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// The sub-line under the chip: where the row came from, plus the one fact that matters
// most for this kind of relationship.
export const relationshipDetail = (relationship: Relationship): string => {
  const parts: string[] = [];
  parts.push(
    relationship.source === "hubspot_only"
      ? "HubSpot only"
      : relationship.source === "apollo_and_hubspot"
        ? "Apollo + HubSpot"
        : "Apollo only"
  );
  if (relationship.kind === "do_not_contact") {
    parts.push("opted out in HubSpot");
  } else if (
    relationship.kind === "moved" &&
    relationship.contact?.companyName
  ) {
    parts.push(`known from ${relationship.contact.companyName}`);
  } else if (relationship.contact?.lastContacted) {
    parts.push(
      `last contacted ${formatLastContacted(relationship.contact.lastContacted)}`
    );
  } else if (relationship.kind === "apollo_only") {
    parts.push("never contacted");
  }
  return parts.join(" · ");
};
